import { Component, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { GridComponent, RowSelectEventArgs, GridModule } from '@syncfusion/ej2-angular-grids';
import { ClientDTO, ExportInterfaceFieldValueReplacementEntryDTO, ExportInterfaceSaveFieldValueReplacementsDTO } from 'src/app/base/models/models.swagger.generated';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { FieldValueReplacementService } from '../field-value-replacement.service';
import { ExportConfigurationMasterDataServiceService } from '../../export-configuration-master-data.service';
import { FieldValueReplacementEntry, IFieldValueReplacementEntry, IValueChangedInfo } from './field-value-replacement-list-editor/field-value-replacement-list-editor.interfaces';
import { ISourceFieldReplacementEntries } from './field-value-replacement-details.interfaces';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { FieldValueReplacementListEditorComponent } from './field-value-replacement-list-editor/field-value-replacement-list-editor.component';
import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';

@Component({
    selector: 'app-field-value-replacement-details',
    templateUrl: './field-value-replacement-details.component.html',
    styleUrls: ['./field-value-replacement-details.component.scss'],
    imports: [DropDownListModule, GridModule, QueryBuilderModule, FieldValueReplacementListEditorComponent, TooltipModule, TranslateModule]
})
export class FieldValueReplacementDetailsComponent {
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly fieldValueReplacementService = inject(FieldValueReplacementService);
  private readonly masterDataServiceService = inject(ExportConfigurationMasterDataServiceService);

  public readonly fieldsGrid = viewChild.required<GridComponent>('fieldsGrid');

  public readonly selectedGridClient = input<ClientDTO>();

  public readonly allClientReplacementsDeleted = output<void>();

  public readonly fieldGridSelectionOptions = { type: 'Single', mode: 'Row' };
  public readonly fieldsGridCommands = [{ type: 'Delete', buttonOption: { cssClass: 'e-flat', iconCss: 'e-delete e-icons' } }];

  public readonly dropDownSourceFields = signal<string[]>([]);
  public readonly selectedDropDownSourceField = signal<string | null>(null);
  public readonly clientSourceFieldsReplacementData = signal<ISourceFieldReplacementEntries[] | null>(null);
  public readonly selectedField = signal<ISourceFieldReplacementEntries | null>(null);
  public readonly hasChanges = signal<boolean>(false);
  public readonly fieldValueReplacementsForSource = signal<IFieldValueReplacementEntry[] | null>(null);

  private fieldGridSelectionIndexAfterRefresh = -1;
  private saveData: ExportInterfaceSaveFieldValueReplacementsDTO | null = null;

  constructor() {
    effect(() => {
      const client = this.selectedGridClient();
      untracked(() => {
        this.saveData = new ExportInterfaceSaveFieldValueReplacementsDTO({
          clientId: client?.id,
          deletedSourceFields: [],
          deletedReplacementEntries: [],
          newOrUpdatedEntries: []
        });
        this.dropDownSourceFields.set([]);
        this.selectedDropDownSourceField.set(null);
        this.selectedField.set(null);
        this.fieldValueReplacementsForSource.set(null);
        if (client != null) {
          this.loadSourceFields();
        } else {
          this.clientSourceFieldsReplacementData.set([]);
          this.fieldsGrid().refresh();
        }
      });
    });
  }

  public onFieldSelected(args: RowSelectEventArgs | { data: ISourceFieldReplacementEntries | null; target?: never }): void {
    if ((args as RowSelectEventArgs).target?.classList.contains('e-delete')) {
      // delete button clicked -> ignore
      return;
    }
    const field = args.data as ISourceFieldReplacementEntries;
    this.selectedField.set(field);
    if (field == null) {
      this.fieldValueReplacementsForSource.set(null);
      return;
    }

    if (field.replacementEntries != null) {
      this.fieldValueReplacementsForSource.set(field.replacementEntries);
    } else {
      this.fieldValueReplacementService.getFieldValueReplacementEntriesForSourceField(this.selectedGridClient()?.id!, field.sourceField).subscribe((values: ExportInterfaceFieldValueReplacementEntryDTO[]) => {
        if (values) {
          const entries: FieldValueReplacementEntry[] = [];
          values.forEach((value: ExportInterfaceFieldValueReplacementEntryDTO) => {
            if (value.sourceField === field.sourceField) {
              entries.push(new FieldValueReplacementEntry(value));
            }
          });
          field.replacementEntries = entries;
          this.fieldValueReplacementsForSource.set(entries);
        } else {
          this.fieldValueReplacementsForSource.set([]);
        }
      });
    }
  }

  public onSave() {
    if (!this.selectedGridClient()) {
      return;
    }
    // check if duplicate replacement source value exist
    const currentReplacements = this.fieldValueReplacementsForSource();
    if (currentReplacements) {
      const duplicateSourceValues = currentReplacements.filter((value, index, self) => self.findIndex(v => v.sourceValue === value.sourceValue) !== index);
      if (duplicateSourceValues.length > 0) {
        const duplicateSourceValuesString = duplicateSourceValues.map(v => v.sourceValue).join(', ');
        var message = this.translate.instant('i18n.domain.export-configuration.field-value-replacements.field-replacement-grid.duplicate-source-values-message', { param: duplicateSourceValuesString });
        this.modalService.showMessageDialog('i18n.domain.export-configuration.field-value-replacements.field-replacement-grid.duplicate-source-values-title', message);
        return;
      }
    }
    this.saveData!.newOrUpdatedEntries = [];
    let invalidSourceField: string | null = null;
    (this.clientSourceFieldsReplacementData() ?? []).forEach((data: ISourceFieldReplacementEntries) => {
      if (invalidSourceField != null) {
        return;
      }
      data.replacementEntries?.forEach((entry: FieldValueReplacementEntry) => {
        if (entry.id <= 0 || entry.sourceValue != entry.origSourceValue || entry.replacementValue != entry.origReplacementValue) {
          if (entry.id <= 0 && !this.hasValidValue(entry.sourceValue) && !this.hasValidValue(entry.replacementValue)) {
            // ignore empty new entries
            return;
          }
          if (entry.sourceValue == null || entry.sourceValue.trim().length == 0 || entry.replacementValue == null || entry.replacementValue.trim().length == 0) {
            invalidSourceField = data.sourceField;
            return;
          }

          this.saveData!.newOrUpdatedEntries!.push(new ExportInterfaceFieldValueReplacementEntryDTO({
            id: entry.id <= 0 ? 0 : entry.id,
            sourceField: data.sourceField,
            sourceValue: entry.sourceValue,
            replacementValue: entry.replacementValue
          }));
        }
      });
    });

    if (invalidSourceField != null) {
      this.saveData!.newOrUpdatedEntries = [];
      this.modalService.showMessageDialog('i18n.domain.export-configuration.field-value-replacements.invalid-replacement-data-title',
        this.translate.instant('i18n.domain.export-configuration.field-value-replacements.invalid-replacement-data-message', { param: invalidSourceField }));
      return;
    }

    this.fieldValueReplacementService.saveFieldValueReplacements(this.saveData!).subscribe(() => {
      this.initializeSaveData(this.selectedGridClient()!.id);
      this.loadSourceFields();
      this.hasChanges.set(false);
    });
  }

  public fieldGridCommandClicked(args: any): void {
    if (args.commandColumn.type == 'Delete') {
      const data = this.clientSourceFieldsReplacementData() ?? [];
      const index = data.findIndex(f => f.sourceField == args.rowData.sourceField);
      if (index > -1) {
        const removingData = data[index];
        data.splice(index, 1); // we need to remove the data from the list first before calling resetHasChangesFlagIfNecessary
        let hasSourceStoredFieldReplacements = removingData.replacementEntries == null; // we received source field from backend but lazy loading yet
        if (!hasSourceStoredFieldReplacements) {
          hasSourceStoredFieldReplacements = removingData.replacementEntries!.find(f => f.id > 0) != null; // more than only newly created records exist
        }
        if (hasSourceStoredFieldReplacements) {
          this.saveData!.deletedSourceFields!.push(args.rowData.sourceField);
          this.hasChanges.set(true);
        } else {
          this.resetHasChangesFlagIfNecessary();
        }

        if (this.selectedField()?.sourceField == args.rowData.sourceField) {
          this.selectedField.set(null);
          this.fieldValueReplacementsForSource.set(null);
        }

        this.updateDropDownSourceFields();
        this.fieldsGrid().refresh();
      }
    }
  }

  public onFieldGridActionComplete($event: any) {
    if ($event.requestType === 'refresh' && this.fieldGridSelectionIndexAfterRefresh > -1) {
      this.fieldsGrid().selectRow(this.fieldGridSelectionIndexAfterRefresh);
    }
  }

  public discardChanges() {
    this.loadSourceFields();
  }

  public onSourceFieldDropDownChange(args: any): void {
    if (args.value) {
      this.selectedDropDownSourceField.set(args.value);
      this.addField();
    }
  }

  public deleteFieldValueReplacementsForClient() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.export-configuration.field-value-replacements.delete-confirm-title', 'i18n.domain.export-configuration.field-value-replacements.delete-confirm-message');
    dialog.onOkSubject.subscribe(() => {
      this.fieldValueReplacementService.deleteAllFieldValueReplacementsForClient(this.selectedGridClient()?.id!).subscribe(data => {
        this.allClientReplacementsDeleted.emit();
      });
    });
  }

  public onSourceValueDeleted(id: number) {
    // splice is handled in the list editor -> we only need to handle here hasChange flag and deletion of stored data
    if (id > 0) {
      this.saveData!.deletedReplacementEntries!.push(id);
      this.hasChanges.set(true);
    } else {
      this.resetHasChangesFlagIfNecessary();
    }
  }

  public onLinesCreated(items: FieldValueReplacementEntry[]) {
    // lines are added in the list editor -> we only need to handle here hasChange flag
    this.hasChanges.set(true);
  }

  public onSourceValueChanged(updateData: IValueChangedInfo) {
    const item = this.fieldValueReplacementsForSource()?.find(f => f.id == updateData.id);
    if (item) {
      item.sourceValue = updateData.newValue;
      if (item.sourceValue != item.origSourceValue) {
        this.hasChanges.set(true);
      }
      this.resetHasChangesFlagIfNecessary();
    }
  }

  public onReplacementValueChanged(updateData: IValueChangedInfo) {
    const item = this.fieldValueReplacementsForSource()?.find(f => f.id == updateData.id);
    if (item) {
      item.replacementValue = updateData.newValue;
      if (item.replacementValue != item.origReplacementValue) {
        this.hasChanges.set(true);
      }
      this.resetHasChangesFlagIfNecessary();
    }
  }

  private addField() {
    const sourceField = this.selectedDropDownSourceField();
    if (sourceField == null) return;
    const data = this.clientSourceFieldsReplacementData() ?? [];
    data.push({ sourceField, replacementEntries: [] });
    data.sort((a, b) => a.sourceField.localeCompare(b.sourceField));
    const index = data.findIndex(f => f.sourceField == sourceField);
    this.fieldGridSelectionIndexAfterRefresh = index;
    this.fieldsGrid().refresh();
    this.dropDownSourceFields.update(fields => fields.filter(f => f != sourceField));
    this.selectedDropDownSourceField.set(null);
  }

  private updateDropDownSourceFields() {
    const data = this.clientSourceFieldsReplacementData() ?? [];
    this.dropDownSourceFields.set(
      this.masterDataServiceService.getAllSourceFields().filter(f => !data.find(fhr => fhr.sourceField == f))
    );
  }

  private initializeSaveData(clientId: number | undefined) {
    this.saveData = new ExportInterfaceSaveFieldValueReplacementsDTO({
      clientId: clientId,
      deletedSourceFields: [],
      deletedReplacementEntries: [],
      newOrUpdatedEntries: []
    });
  }

  private loadSourceFields() {
    const clientId = this.selectedGridClient()?.id;
    if (clientId == null) return;
    this.clientSourceFieldsReplacementData.set(null); // signal set to null forces Syncfusion grid to reset before reload
    const currentField = this.selectedField();
    this.fieldValueReplacementService.getSourceFieldsWithFieldValueReplacements(clientId).subscribe((sourceFields: string[]) => {
      const mapped: ISourceFieldReplacementEntries[] = sourceFields?.map((value) => {
        return { sourceField: value, replacementEntries: null };
      });
      this.clientSourceFieldsReplacementData.set(mapped ?? []);
      this.updateDropDownSourceFields();
      this.fieldsGrid().refresh();
      if (currentField) {
        const reselected = mapped?.find(f => f.sourceField == currentField.sourceField) ?? null;
        this.selectedField.set(reselected);
        this.onFieldSelected({ data: reselected });
      }
      this.hasChanges.set(false);
    });
  }

  private resetHasChangesFlagIfNecessary() {
    if (!this.hasChanges()) {
      // flag already reset -> so ignore
      return;
    }

    if (this.saveData!.deletedSourceFields!.length > 0 || this.saveData!.deletedReplacementEntries!.length > 0) {
      return;
    }
    const firstSourceFieldWithChanges = (this.clientSourceFieldsReplacementData() ?? []).find((data: ISourceFieldReplacementEntries) => {
      if (!data.replacementEntries) {
        return false;
      }
      return data.replacementEntries.find((entry: FieldValueReplacementEntry) => {
        return (entry.id <= 0 && (this.hasValidValue(entry.sourceValue) || this.hasValidValue(entry.replacementValue))) || (entry.id > 0 && (entry.sourceValue != entry.origSourceValue || entry.replacementValue != entry.origReplacementValue));
      }) != null;
    });
    if (!firstSourceFieldWithChanges) {
      this.hasChanges.set(false);
    }
  }

  private hasValidValue(value: string) {
    return value != null && value.trim().length > 0;
  }
}
